// src/components/captures/CaptureTimelineView.tsx
import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { Capture } from '../../types/capture';
import CaptureCard from './CaptureCard';
import { toDateObject } from '../../utils/dateUtils';
import { NotebookPen } from '../common/icons';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { logWarn, logInfo } from '../../utils/logger';

interface CaptureTimelineViewProps {
  captures: Capture[];
  onUpdate: () => void;
  onDelete: (captureId: string) => void;
  // 無限滾動相關
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  // Lesson 模式
  isLessonMode?: boolean;
  lessonSeedCaptureId?: string;
}

const CaptureTimelineView: React.FC<CaptureTimelineViewProps> = ({
  captures,
  onUpdate,
  onDelete,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  isLessonMode = false,
  lessonSeedCaptureId,
}) => {
  // 直接使用傳入的 captures（已在父組件篩選）
  const filteredCaptures = captures;
  
  // 檢測是否為行動版
  const isMobile = useMediaQuery('(max-width: 640px)');
  
  // 無限滾動相關
  const loadingTriggerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 格式化日期標題 - 移到組件外層避免重複創建
  const formatDateTitle = useCallback((key: string, dateObj: Date): string => {
    if (key === 'today') return 'Today';
    if (key === 'yesterday') return 'Yesterday';

    // 格式化其他日期
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    };

    return dateObj.toLocaleDateString('en-US', options);
  }, []);

  // 使用 useMemo 快取日期分組邏輯，只有當 filteredCaptures 改變時才重新計算
  const { groupedCaptures, sortedDateKeys } = useMemo(() => {
    const groups: Record<string, { captures: Capture[]; dateObj: Date }> = {};

    // 計算今天和昨天的日期字符串，避免在循環中重複計算
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const todayString = today.toDateString();
    const yesterdayString = yesterday.toDateString();

    filteredCaptures.forEach(capture => {
      if (!capture.createdAt) return; // 跳過沒有日期的 capture

      try {
        const dateObj = toDateObject(capture.createdAt);
        const dateString = dateObj.toDateString();

        let key: string;

        // 判斷日期類型 - 使用預計算的字符串比較
        if (dateString === todayString) {
          key = 'today';
        } else if (dateString === yesterdayString) {
          key = 'yesterday';
        } else {
          // 使用完整日期作為 key
          key = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD
        }

        if (!groups[key]) {
          groups[key] = {
            captures: [],
            dateObj: dateObj
          };
        }

        groups[key].captures.push(capture);
      } catch (error) {
        logWarn('無效的 capture 日期:', capture.id, error);
      }
    });

    // 獲取排序後的日期鍵值（今天、昨天、然後是最新的日期）
    const keys = Object.keys(groups);
    const sortedKeys = keys.sort((a, b) => {
      // 今天排最前
      if (a === 'today') return -1;
      if (b === 'today') return 1;

      // 昨天排第二
      if (a === 'yesterday') return -1;
      if (b === 'yesterday') return 1;

      // 其他日期按時間倒序
      const dateA = groups[a].dateObj;
      const dateB = groups[b].dateObj;
      return dateB.getTime() - dateA.getTime();
    });

    return {
      groupedCaptures: groups,
      sortedDateKeys: sortedKeys
    };
  }, [filteredCaptures]); // 只依賴 filteredCaptures

  // 無限滾動處理
  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore && onLoadMore) {
      logInfo('觸發載入更多 Capture');
      onLoadMore();
    }
  }, [isLoadingMore, hasMore, onLoadMore]);

  // 設置 Intersection Observer
  useEffect(() => {
    if (!loadingTriggerRef.current || !onLoadMore) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        threshold: 0.1, // 當觸發器 10% 可見時就載入
        rootMargin: '100px' // 提前 100px 觸發載入
      }
    );

    observerRef.current.observe(loadingTriggerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleLoadMore, onLoadMore]);

  // 渲染空狀態
  const renderEmptyState = () => {
    return (
      <div className="flex flex-col items-center justify-center h-60">
        <NotebookPen className="h-12 w-12 text-gray-400 mb-3" />
        <p className="text-gray-400 font-medium">No captures</p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 時間軸容器 */}
      {sortedDateKeys.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="relative">
          {sortedDateKeys.map((dateKey) => {
            const group = groupedCaptures[dateKey];
            
            return (
              <div key={dateKey} className="relative mb-10">
                {/* 時間軸線 - 只在桌面版顯示 */}
                {!isMobile && (
                  <div
                    className="absolute left-3 top-10 w-0.5 bg-gradient-to-b from-gray-300 to-transparent"
                    style={{ 
                      transform: 'translateX(-50%)',
                      height: '100%'
                    }}
                  />
                )}

                {/* 日期標題 */}
                <div className={`flex items-center mb-5 ${!isMobile ? 'gap-4' : 'justify-between'}`}>
                  {/* 時間軸標記點 - 只在桌面版顯示 */}
                  {!isMobile && (
                    <div className="relative z-10">
                      <div className="w-6 h-6 bg-white border-2 border-sky-500 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-sky-500 rounded-full"></div>
                      </div>
                      {/* 外暈效果 */}
                      <div className="absolute inset-0 w-8 h-8 -m-1 bg-sky-100 rounded-full -z-10 opacity-20"></div>
                    </div>
                  )}

                  {/* 日期資訊 */}
                  {isMobile ? (
                    // 行動版：單行顯示，統計靠右
                    <>
                      <h3 className="text-base font-semibold text-gray-700">
                        {formatDateTitle(dateKey, group.dateObj)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {group.captures.length} capture{group.captures.length !== 1 ? 's' : ''}
                      </p>
                    </>
                  ) : (
                    // 桌面版：保持原樣
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-700">
                        {formatDateTitle(dateKey, group.dateObj)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {group.captures.length} capture{group.captures.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                </div>

                {/* 該日期的 Captures */}
                <div className={`space-y-4 ${!isMobile ? 'ml-10' : ''}`}>
                  {group.captures.map(capture => (
                    <CaptureCard
                      key={capture.id}
                      capture={capture}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      isLessonMode={isLessonMode}
                      isSeedCapture={isLessonMode && capture.id === lessonSeedCaptureId}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          
          {/* 無限滾動觸發器和載入指示器 */}
          {onLoadMore && (
            <div className="mt-8">
              {hasMore && (
                <>
                  {/* 載入觸發器（隱形） */}
                  <div
                    ref={loadingTriggerRef}
                    className="h-1 w-full"
                  />
                  
                  {/* 載入指示器 */}
                  {isLoadingMore && (
                    <div className="flex flex-col items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                      <p className="mt-3 text-sm text-gray-500">Loading more captures...</p>
                    </div>
                  )}
                </>
              )}
              
              {/* 沒有更多數據時的提示 */}
              {!hasMore && captures.length > 0 && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-3 text-sm text-gray-400">
                    <div className="w-16 h-px bg-gray-300"></div>
                    <span>You've reached the end</span>
                    <div className="w-16 h-px bg-gray-300"></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CaptureTimelineView;